import { darken, Stack, Tooltip } from "@mui/material";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { StatusChip } from "@sinch/components/StatusChip/StatusChip";
import { useRoleStatus } from "@sinch/components/application/Worker/useRoleStatus";
import { RoleStatusEnum } from "@sinch/core/entities/serverEnums";
import { StaffHeaderFragment } from "./fragments.types";

/**
 * Row of status indicators for a worker: role status plus penalty / conditional
 * penalty / unapproved badges. The row hides itself when it would be empty.
 */
export const StaffStatusChips: FC<{ staff: StaffHeaderFragment }> = ({ staff }) => {
  const { t } = useTranslation();

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ "&:empty": { display: "none" } }}>
      {staff.roleStatus && <UserStatus status={staff.roleStatus} />}
      {staff.hasAgencyBans && (
        <Tooltip title={t("Staff.penaltyDescription")}>
          <StatusChip color="error" label={t("Staff.penalty")} size="tiny" />
        </Tooltip>
      )}
      {staff.hasPenalty && (
        <Tooltip title={t("Staff.conditionalPenaltyDescription")}>
          <StatusChip color="error" label={t("Staff.conditionalPenalty")} size="tiny" />
        </Tooltip>
      )}
      {staff.workerinfo?.verified === false && (
        <Tooltip title={t("Staff.unapprovedDescription")}>
          <StatusChip color="warning" label={t("Staff.unapproved")} size="tiny" />
        </Tooltip>
      )}
    </Stack>
  );
};

const UserStatus = ({ status }: { status: RoleStatusEnum | `${RoleStatusEnum}` }) => {
  const { getRoleStatusColor, getRoleStatusLabel } = useRoleStatus();

  return (
    <StatusChip
      label={getRoleStatusLabel(status)}
      size="tiny"
      sx={(theme) => ({
        backgroundColor: getRoleStatusColor(status),
        color: theme.palette.getContrastText(darken(getRoleStatusColor(status), 0.1)),
      })}
    />
  );
};
